import type { Token, TokenType, IQLSets } from "./types";
import { TOKEN_COLORS, CAT_COLOR } from "./themes";
import vocabulary from "./vocabulary.json";

// Module-level sets built once from vocabulary — never recreated at runtime
const KEYWORDS = new Set(vocabulary.keywords);
const CATEGORY_STRINGS = new Set(vocabulary.categories);

// Word-form operators (identifiers like "contains", "BIT_AND") — symbolic ones are
// handled by the symOp regex branch, so filter those out here
const VOCAB_TEXT_OPS = new Set(
  vocabulary.operators.filter((op) => /^[A-Za-z_]/.test(op)),
);

const EMPTY_SETS: IQLSets = {
  entities: new Set(vocabulary.entities),
  errEntities: new Set(),
  pipeFns: new Set(vocabulary.functions),
  textOps: VOCAB_TEXT_OPS,
};

/**
 * Tokenizes a single line of IQL. Accepts pre-built IQLSets to avoid
 * recreating Set objects on every keystroke (see useMemo in CodeEditor).
 */
export function tokenizeLine(
  line: string,
  declaredVars: Set<string>,
  sets: IQLSets = EMPTY_SETS,
): Token[] {
  const { entities, errEntities, pipeFns, textOps } = sets;
  const tokens: Token[] = [];
  let i = 0;

  const commentMatch = line.match(/^(\s*)(--.*)/);
  if (commentMatch) {
    if (commentMatch[1]) tokens.push({ type: "field", value: commentMatch[1] });
    tokens.push({ type: "comment", value: commentMatch[2] });
    return tokens;
  }

  while (i < line.length) {
    const rest = line.slice(i);

    const wsMatch = rest.match(/^(\s+)/);
    if (wsMatch) {
      tokens.push({ type: "field", value: wsMatch[1] });
      i += wsMatch[1].length;
      continue;
    }

    const nowMatch = rest.match(/^(\$now(?:\s*[+-]\s*\d+\s*days?)?)/);
    if (nowMatch) {
      tokens.push({ type: "time", value: nowMatch[1] });
      i += nowMatch[1].length;
      continue;
    }

    const timeShort = rest.match(/^(\d+[dh])(?=\b)/);
    if (timeShort) {
      tokens.push({ type: "time", value: timeShort[1] });
      i += timeShort[1].length;
      continue;
    }

    if (rest[0] === "'") {
      const closeIdx = rest.indexOf("'", 1);
      if (closeIdx === -1) {
        tokens.push({ type: "partialStr", value: rest });
        i += rest.length;
      } else {
        const inner = rest.slice(1, closeIdx);
        if (CATEGORY_STRINGS.has(inner)) {
          tokens.push({
            type: "catStr",
            value: rest.slice(0, closeIdx + 1),
            inner,
          });
        } else {
          tokens.push({ type: "str", value: rest.slice(0, closeIdx + 1) });
        }
        i += closeIdx + 1;
      }
      continue;
    }

    const symOp = rest.match(/^(==|!=|>=|<=|&&|\|\||[><])/);
    if (symOp) {
      tokens.push({ type: "op", value: symOp[1] });
      i += symOp[1].length;
      continue;
    }

    if (rest[0] === "!") {
      tokens.push({ type: "op", value: "!" });
      i++;
      continue;
    }

    if (rest[0] === "|" && rest[1] !== "|") {
      tokens.push({ type: "pipe", value: "|" });
      i++;
      continue;
    }

    const numMatch = rest.match(/^(\d+(?:\.\d+)?)/);
    if (numMatch) {
      tokens.push({ type: "number", value: numMatch[1] });
      i += numMatch[1].length;
      continue;
    }

    if ("();.,[]".includes(rest[0])) {
      tokens.push({ type: "punct", value: rest[0] });
      i++;
      continue;
    }

    const wordMatch = rest.match(/^([\w$][\w$.[\]*]*)/);
    if (wordMatch) {
      const word = wordMatch[1];
      i += word.length;
      if (KEYWORDS.has(word)) tokens.push({ type: "keyword", value: word });
      else if (entities.has(word)) tokens.push({ type: "entity", value: word });
      else if (errEntities.has(word))
        tokens.push({ type: "errEntity", value: word });
      else if (pipeFns.has(word)) tokens.push({ type: "fn", value: word });
      else if (textOps.has(word)) tokens.push({ type: "textOp", value: word });
      else if (declaredVars.has(word))
        tokens.push({ type: "variable", value: word });
      else tokens.push({ type: "field", value: word });
      continue;
    }

    tokens.push({ type: "field", value: rest[0] });
    i++;
  }

  return tokens;
}

export function tokenColor(type: TokenType): string {
  return TOKEN_COLORS[type];
}

export function renderToken(tok: Token, idx: number): React.ReactNode {
  const color = TOKEN_COLORS[tok.type];

  if (tok.type === "catStr" && tok.inner) {
    return (
      <span key={idx} style={{ color: TOKEN_COLORS.str }}>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {"'"}
        <span style={{ color: CAT_COLOR }}>{tok.inner}</span>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {"'"}
      </span>
    );
  }

  if (tok.type === "partialStr") {
    return (
      <span key={idx} style={{ color, opacity: 0.5 }}>
        {tok.value}
      </span>
    );
  }

  if (tok.type === "errEntity") {
    return (
      <span
        key={idx}
        style={{
          color,
          textDecoration:
            "underline wavy var(--color-foreground-critical-medium)",
        }}
      >
        {tok.value}
      </span>
    );
  }

  if (tok.type === "comment") {
    return (
      <span key={idx} style={{ color, fontStyle: "italic" }}>
        {tok.value}
      </span>
    );
  }

  const fw = tok.type === "keyword" || tok.type === "entity" ? 500 : 400;
  return (
    <span key={idx} style={{ color, fontWeight: fw }}>
      {tok.value}
    </span>
  );
}
